<template>
  <v-card v-if="loaded" class="mt-3" color="blue-grey lighten-5" elevation="0">
    <v-card-text>
      <v-row>
        <v-col lg="3" md="6" cols="12">
          <v-card elevation="1" height="300">
            <v-card-title class="text-h5">
              {{ $tc('route_plan', 2) }}
              <info-modal width="800">
                <v-card-title class="text-h5">
                  {{ $t('performance_dashboard_ui.modal_info.route_plans.unlinked.title') }}
                </v-card-title>
                <v-card-text v-html="$t('performance_dashboard_ui.modal_info.route_plans.unlinked.text')"/>
              </info-modal>
            </v-card-title>
            <v-card-subtitle>
              {{ $t('performance_dashboard_ui.past_30_days' )}}
            </v-card-subtitle>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-subtitle-1" v-if="overallResults.routePlans.total === 0">
                {{ $t('no_data_available') }}
              </div>
              <v-list class="py-0" v-else>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-link</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('linked') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="success" dark>
                      {{ overallResults.routePlans.linked.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-link-off</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('unlinked') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="danger" dark>
                      {{ overallResults.routePlans.unlinked.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-upload</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('uploaded') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="blue-grey" dark>
                      {{ overallResults.routePlans.total.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col lg="3" md="6" cols="12">
          <v-card elevation="1" height="300">
            <v-card-title class="text-h5">
              {{ $tc('completed_stop', 2) }}
              <info-modal width="800">
                <v-card-title class="text-h5">
                  {{ $t('performance_dashboard_ui.modal_info.route_plans.missed_stops.title') }}
                </v-card-title>
                <v-card-text v-html="$t('performance_dashboard_ui.modal_info.route_plans.missed_stops.text')"/>
              </info-modal>
            </v-card-title>
            <v-card-subtitle>
              {{ $t('performance_dashboard_ui.past_30_days' )}}
            </v-card-subtitle>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-subtitle-1" v-if="overallResults.stops.planned === 0">
                {{ $t('no_data_available') }}
              </div>
              <v-list class="py-0" v-else>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-timer</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('completed') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="success" dark>
                      {{ overallResults.stops.completed.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-timer-off</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('missed') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="danger" dark>
                      {{ overallResults.stops.missed.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-notebook</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('planned') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="blue-grey" dark>
                      {{ overallResults.stops.planned.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <v-card elevation="1" height="300">
            <v-card-title class="text-h5 d-flex justify-space-between">
              <div class="d-flex align-center">
                <span>
                  {{ $t('performance_dashboard_ui.daily_trend') }}
                  <span class="six-months-subheader">{{ $t('performance_dashboard_ui.trailing_six_months') }}</span>
                </span>
                <info-modal width="800">
                  <v-card-title class="text-h5">
                    {{ $t('performance_dashboard_ui.modal_info.route_plans.six_month_trend.title') }}
                  </v-card-title>
                  <v-card-text v-html="$t('performance_dashboard_ui.modal_info.route_plans.six_month_trend.text')"/>
                </info-modal>
              </div>
              <v-btn color="primary" @click="generateReport">
                <v-icon>mdi-chart-areaspline</v-icon>
                {{ $t('performance_dashboard_ui.see_full_report') }}
              </v-btn>
            </v-card-title>
            <v-card-text>
              <route-plans-chart v-if="loaded" ref="route-plans-chart" :selected-customers="selectedCustomers" :height="220"/>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-card v-if="tableData.length > 1" class="mt-4" elevation="1">
        <v-card-title class="text-h5">
          {{ $t('routes_by_dc' )}}
          <info-modal width="800" v-if="false">
            <v-card-title class="text-h5">
              {{ $t('performance_dashboard_ui.modal_info.fleet_health.distribution_centers.title') }}
            </v-card-title>
            <v-card-text v-html="$t('performance_dashboard_ui.modal_info.fleet_health.distribution_centers.text')"/>
          </info-modal>
        </v-card-title>
        <v-divider></v-divider>
        <v-data-table
          class="vf-table"
          :items="tableData"
          :headers="tableFields"
          sort-by="linked_percent">
          <template #item.name="row">
            <router-link :to="`/performance/dashboard/customers/${row.item.id}`">{{ row.value }}</router-link>
          </template>
          <template #item.linked_percent="row">
            {{ row.value }}%
          </template>
          <template #item.completed_percent="row">
            {{ row.value }}%
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>
  </v-card>
  <v-card v-else class="mt-3" color="blue-grey lighten-5" elevation="0">
    <v-card-text>
      <v-row>
        <v-col lg="3" md="6" cols="12">
          <v-skeleton-loader type="card" />
        </v-col>
        <v-col lg="3" md="6" cols="12">
          <v-skeleton-loader type="card" />
        </v-col>
        <v-col>
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-skeleton-loader type="table"/>
  </v-card>
</template>

<script>
import RoutePlansChart from '@/components/performance-dashboard/RoutePlansChart'
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import PerformanceDashboardComponentMixin from '@/components/performance-dashboard/PerformanceDashboardComponentMixin'
import { each, get, invoke, map } from 'lodash'
import Moment from 'moment-timezone'
import MathUtil from '@/util/MathUtil'

export default {
  name: 'RoutePlansDashboard',
  components: {
    RoutePlansChart,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    PerformanceDashboardComponentMixin
  ],
  data () {
    return {
      loaded: false,
      routesData: null,
      routePlansData: null,
      routeStopsData: null,
      // the maximum number of customers to display in the most/fewest sections.
      displayLimit: 10,
      tableFields: [
        {
          value: 'name',
          text: this.$t('name'),
          sortable: true
        },
        {
          value: 'linked_percent',
          text: this.$tc('linked_plans'),
          sortable: true
        },
        {
          value: 'completed_percent',
          text: this.$tc('completed_stop', 2),
          sortable: true
        }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    /**
     * Load the data from the API.
     */
    async loadData () {
      this.loaded = false
      this.$emit('loading')
      const requestedCustomerIds = this.requestedCustomerIds.join(',')
      try {
        const routesResult = await this.$httpAdapter.get('/customer-insights/routes', {
          params: {
            customer_id: requestedCustomerIds
          }
        })
        this.routesData = routesResult.data

        const routePlansResult = await this.$httpAdapter.get('/customer-insights/route-plans', {
          params: {
            customer_id: requestedCustomerIds
          }
        })
        this.routePlansData = routePlansResult.data

        const routeStopsResult = await this.$httpAdapter.get('/customer-insights/route-stops', {
          params: {
            customer_id: requestedCustomerIds
          }
        })
        this.routeStopsData = routeStopsResult.data
        this.$nextTick(() => {
          this.$emit('loaded', {
            value: this.overallResults.routePlans.percent_linked
          })
        })
      } catch (error) {
        this.$emit('errorLoading')
        this.handleError(error)
      } finally {
        this.loaded = true
      }
    },
    /**
     * Generate the full Route Plans History report and redirect to it.
     */
    async generateReport () {
      const reportParameters = {}
      reportParameters.startDate = Moment.tz(this.customerTimezone).subtract(6, 'months').format('YYYY-MM-DD')
      reportParameters.endDate = Moment.tz(this.customerTimezone).format('YYYY-MM-DD')
      reportParameters.customerIds = map(this.selectedCustomers, x => x.id)
      const report = this.$orm('Report').createRecord({
        type: 'RoutePlanHistoryReport',
        name: this.$t('performance_dashboard_ui.route_plan_history_report'),
        parameters: reportParameters
      })
      const savedReport = await this.$orm('Report').createOrUpdate(report)
      await this.$router.push(`/report/${savedReport.id}`)
    }
  },
  computed: {
    /**
     * Tabulate results across all the selected customers.
     * @returns Object|null
     */
    overallResults () {
      if (this.loaded && this.routePlansData != null && this.routeStopsData != null) {
        const results = {
          percent: 0,
          status: 'no_data',
          icon: 'question',
          routePlans: {
            total: 0,
            linked: 0,
            unlinked: 0
          },
          routes: {
            total: 0,
            assigned: 0,
            unassigned: 0
          },
          stops: {
            planned: 0,
            missed: 0,
            completed: 0,
            unplanned: 0
          }
        }
        each(this.routesData, customer => {
          results.routes.total += customer.num_total_routes
          results.routes.assigned += customer.num_assigned_routes
          results.routes.unassigned += customer.num_unassigned_routes
        })
        each(this.routePlansData, customer => {
          results.routePlans.total += customer.total_route_plans
          results.routePlans.linked += customer.linked_route_plans
          results.routePlans.unlinked += customer.unlinked_route_plans
        })
        each(this.routeStopsData, customer => {
          results.stops.planned += customer.planned_stops
          results.stops.missed += customer.missed_stops
          results.stops.completed += (customer.planned_stops - customer.missed_stops)
          results.stops.unplanned += customer.unplanned_stops
        })
        results.routePlans.percent_linked = results.routePlans.total === 0 ? 100 : MathUtil.percentage(results.routePlans.linked, results.routePlans.total, null)
        return results
      }
      return null
    },
    /**
     * A list of customers for the tabel that:
     * 1) Has had customer ids and names added in (since these aren't returned with the API)
     * 2) Has customers with no vehicles filtered out
     * 3) Has percentages calculated
     */
    tableData () {
      const customerData = []
      each(this.requestedCustomerIds, customerId => {
        const routes = get(this.routesData, customerId)
        // Filter out all customers with no route data available
        if (routes.num_total_routes > 0) {
          const routePlans = get(this.routePlansData, customerId)
          const routeStops = get(this.routeStopsData, customerId)

          routeStops.completed = routeStops.planned_stops - routeStops.missed_stops

          routes.percent = MathUtil.percentage(routes.num_assigned_routes, routes.num_total_routes, 1)
          routePlans.percent = routePlans.total_route_plans === 0 ? 100 : MathUtil.percentage(routePlans.linked_route_plans, routePlans.total_route_plans, 1);
          routeStops.percent = MathUtil.percentage(routeStops.completed, routeStops.planned_stops, 1)

          customerData.push({
            id: parseInt(customerId),
            name: this.getCustomerName(customerId),
            linked_percent: MathUtil.percentage(routePlans.linked_route_plans, routePlans.total_route_plans, 1),
            completed_percent: MathUtil.percentage(routeStops.completed, routeStops.planned_stops, 1)
          })
        }
      })
      return customerData
    }
  },
  watch: {
    selectedCustomers () {
      this.loadData()
    },
    // When tab becomes active, force the charts to redraw (in case the window size changed while on another tab)
    active (newValue, oldValue) {
      if (newValue === true) {
        invoke(this.$refs, 'route-plans-chart.fit')
      }
    }
  }
}
</script>

<style scoped>

</style>
